import { useQuery } from '@tanstack/react-query'
import { selectUserOpsByAddress, userOpsStore } from '@towns/userops'
import { axiosClient } from 'api/apiClient'
import { useBalance } from 'hooks/useBalance'
import { staleTime24Hours, useConnectivity } from 'use-towns-client'
import { env } from 'utils'
import { Address } from 'viem'
import { useShallow } from 'zustand/react/shallow'

import { useDecodedCallDataWithAddress } from '../UserOpTxModal/hooks/useDecodedCallData'
import { useInsufficientBalance } from '../UserOpTxModal/hooks/useInsufficientBalance'

export function useGetSimpleAccountAddress() {
    const { loggedInWalletAddress } = useConnectivity()

    return useQuery({
        queryKey: ['simpleAccount', loggedInWalletAddress ?? 'WAITING_FOR_WALLET'],
        queryFn: async (): Promise<{
            address: Address
        }> => {
            const stackupWorkerUrl = env.VITE_AA_PAYMASTER_PROXY_URL
            const response = await axiosClient.get(
                `${stackupWorkerUrl}/api/get-simple-address/${loggedInWalletAddress}`,
            )
            return response.data
        },
        select: (data) => {
            return {
                address: data.address,
            }
        },
        enabled: !!loggedInWalletAddress,
        staleTime: staleTime24Hours,
    })
}

export function useOpForSimpleAccount(simpleAccountAddress: Address | undefined) {
    const { data: balance, isLoading: isLoadingBalance } = useBalance({
        address: simpleAccountAddress,
        enabled: !!simpleAccountAddress,
        refetchInterval: 5_000,
    })

    const { current, promptUser, setPromptResponse } = userOpsStore(
        useShallow((s) => ({
            current: selectUserOpsByAddress(simpleAccountAddress, s)?.current,
            promptUser: selectUserOpsByAddress(simpleAccountAddress, s)?.promptUser,
            setPromptResponse: s.setPromptResponse,
        })),
    )

    const currOp = current?.op
    const currOpDecodedCallData = useDecodedCallDataWithAddress(simpleAccountAddress)
    const currOpValue = currOpDecodedCallData?.value

    const balanceIsLessThanCost = useInsufficientBalance({
        balance: balance?.value,
        gasLimit: currOp?.callGasLimit ?? 0.0,
        preVerificationGas: currOp?.preVerificationGas ?? 0.0,
        verificationGasLimit: currOp?.verificationGasLimit ?? 0.0,
        gasPrice: currOp?.maxFeePerGas ?? 0.0,
        value: currOpValue,
        functionHash: 'transferEth',
    })

    return {
        currOp,
        currOpDecodedCallData,
        currOpValue,
        balance,
        balanceIsLessThanCost,
        promptUser,
        setPromptResponse,
        isLoadingBalance,
    }
}
